import React, { useState, useEffect } from "react";
import { Button, Text, useToast } from "@chakra-ui/react";
import dayjs from "dayjs";
import ReusableTable from "./ReusableTable";
import { getPaymentRecord } from "../pages/topUp/services/getPaymentRecord";

const RECORDS_PER_PAGE = 10;

export const PaymentRecordTable = ({
  userId,
  estateId,
  commodityType,
  onRepurchase,
}) => {
  const [records, setRecords] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (!userId) return;
    const skip = (currentPage - 1) * RECORDS_PER_PAGE;
    getPaymentRecord(
      userId,
      setRecords,
      setLoading,
      estateId,
      { limit: RECORDS_PER_PAGE, skip, commodityType },
      setTotalRecords,
    );
  }, [userId, estateId, commodityType, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [estateId, commodityType]);

  const columns = [
    { key: "commodityType", header: "Commodity" },
    {
      key: "createdAt",
      header: "Date",
      render: (row) => (
        <>
          <Text>{dayjs(row?.createdAt).format("DD MMM YYYY")}</Text>
          <Text fontSize="12px" fontWeight="normal">
            Last Payment
          </Text>
        </>
      ),
    },
    {
      key: "meterId",
      header: "Meter",
      render: (row) => (
        <>
          <Text>{row?.meterId}</Text>
          <Text fontSize="12px" fontWeight="normal">
            Meter Reading
          </Text>
        </>
      ),
    },
    {
      key: "unitPurchased",
      header: "Unit",
      render: (row) => (
        <>
          <Text>{row?.unitPurchased}</Text>
          <Text fontSize="12px" fontWeight="normal">
            {row?.commodityType === "WATER"
              ? "Litre"
              : row?.commodityType === "ELECTRICITY"
                ? "Token"
                : row?.commodityType === "GAS"
                  ? "KG"
                  : ""}
          </Text>
        </>
      ),
    },
    {
      key: "token",
      header: "Token",
      render: (row) =>
        row?.token ? (
          <Button
            size="xs"
            variant="outline"
            maxW="140px"
            onClick={() =>
              navigator.clipboard.writeText(row.token).then(() =>
                toast({
                  title: "Token copied!",
                  status: "success",
                  duration: 2000,
                  isClosable: true,
                  position: "top",
                })
              )
            }
            title={row.token}
          >
            <Text isTruncated maxW="120px">
              {row.token}
            </Text>
          </Button>
        ) : (
          <Text fontSize="12px" color="gray.400">
            {row?.status === "PENDING" ? (
              <Text fontSize="11px" color="orange.400" fontWeight="semibold">
                Awaiting token
              </Text>
            ) : row?.status === "FAILED" ? (
              <Text fontSize="11px" color="red.400" fontWeight="semibold">
                Failed
              </Text>
            ) : (
              <Text fontSize="11px" color="gray.400">—</Text>
            )}
          </Text>
        ),
    },
    {
      key: "amountPaid",
      header: "Amount",
      render: (row) => (
        <>
          <Text>{row?.amountPaid}</Text>
          <Text fontSize="12px" fontWeight="normal">
            Total Cost
          </Text>
        </>
      ),
    },
    {
      key: "action",
      header: "Action",
      render: (row) => (
        <Button
          bg="primary.500"
          fontWeight="normal"
          onClick={() => onRepurchase && onRepurchase(row)}
        >
          Re-Purchase
        </Button>
      ),
    },
  ];

  return (
    <ReusableTable
      columns={columns}
      data={records}
      emptyText="No payment records found"
      isLoading={loading}
      enablePagination={true}
      currentPage={currentPage}
      totalPages={Math.ceil(totalRecords / RECORDS_PER_PAGE)}
      onPageChange={(page) => setCurrentPage(page)}
    />
  );
};
